import { useEffect, useId, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { $countryProvincesStore, $countryStore, $recipientCreateStore, $recipientMandatoryFieldsStore, $transferCurrenciesStore } from '@lib/store';
import { TransferRecipientAccountTypeEnum, TransferRecipientTypeEnum } from '@shared/api/rest/recipients';
import { AccountFormBlock, AddressFormBlock, CompanyFormBlock, ConfirmModal } from '@widgets';
import { Button, Spinner } from '@shared/widgets';
import ConfirmIcon from '@assets/icons/confirm.svg';

export interface IFormDataBusinessCreate {
  bankCountry?: string;
  currency?: string;
  accountNumber: string;
  accountType?: TransferRecipientAccountTypeEnum;
  iban: string;
  bic: string;
  firstName: string;
  lastName: string;
  bankName: string;
  idNumber: string;
  companyName: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  companyCountry: string;
  bankProvince?: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  province?: string;
}
const DEFAULT_VALUES = {
  bankCountry: undefined,
  currency: undefined,
  accountNumber: '',
  accountType: undefined,
  iban: '',
  bic: '',
  firstName: '',
  lastName: '',
  idNumber: '',
  bankName: '',
  companyName: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  companyCountry: '',
  bankProvince: undefined,
  bankAddress: '',
  bankCity: '',
  branchCode: '',
  province: undefined,
  amount: undefined,
  paymentReason: '',
} as IFormDataBusinessCreate;

export const BusinessRecipient = () => {
  const recipientStore = useUnit($recipientCreateStore.store);
  const countryStore = useUnit($countryStore.store);
  const recipientMandatoryFieldsStore = useUnit($recipientMandatoryFieldsStore.store);

  const formId = useId();
  const formControl = useForm({ defaultValues: DEFAULT_VALUES });
  const bankCountry = formControl.watch('bankCountry');
  const companyCountry = formControl.watch('companyCountry');
  const currency = formControl.watch('currency');

  const { t } = useTranslation('recipientEditor');

  useEffect(() => {
    $transferCurrenciesStore.events.update();
    $countryStore.events.update();
    $countryProvincesStore.events.update();
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $recipientCreateStore.events.clear();
    };
  }, []);

  const countryOptions = useMemo(() => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })), [countryStore]);
  const BankCountryName = countryOptions.find((option) => option.value === bankCountry);
  const ResidenceCountryName = countryOptions.find((option) => option.value === companyCountry);
  useEffect(() => {
    if (BankCountryName && ResidenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.get({
        ResidenceCountryCode: ResidenceCountryName?.value,
        BankCountryCode: BankCountryName?.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Business,
      });
    }
  }, [BankCountryName, ResidenceCountryName, currency]);

  const onSubmit = async (data: IFormDataBusinessCreate) => {
    await $recipientCreateStore.events.create({
      type: TransferRecipientTypeEnum.Business,
      countryCode: data.companyCountry,
      currency: data.currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: undefined,
      lastName: undefined,
      idNumber: undefined,
      companyName: data.companyName,
      bankName: data.bankName,
      bankAddress: data.bankAddress,
      bankProvince: data.bankProvince,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      bankCountryCode: bankCountry,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
  };

  return (
    <div className='flex w-full flex-col'>
      {recipientStore.loading && <Spinner position />}
      <FormProvider {...formControl}>
        <form id={formId} noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
          <div className='flex flex-col gap-6 bg-white dark:bg-soft-black'>
            <CompanyFormBlock />
            <AddressFormBlock type='Business' />
            <AccountFormBlock />
          </div>
        </form>
        <Button
          className='mt-5 max-w-72 px-28'
          color='primary'
          type='submit'
          form={formId}
          disabled={formControl.formState.isSubmitting || recipientMandatoryFieldsStore.loading}
        >
          {t('buttons.addNewRecipient')}
        </Button>
      </FormProvider>
      <ConfirmModal isOpen={!!recipientStore.data} icon={<ConfirmIcon />} title={t('modals.confirm.title')} />
    </div>
  );
};
